import { useState, useCallback, useRef, useEffect } from 'react';
import { RealtimeChat } from '@/utils/realtimeAudio';
import { OpenAITool } from '@/types/voiceAssistantTools';

interface RealtimeMessage {
  id: string;
  content: string;
  type: 'user' | 'assistant';
  timestamp: Date;
  isSpoken?: boolean;
}

interface UseRealtimeAPIProps {
  systemPrompt?: string;
  onSpeakingChange?: (speaking: boolean) => void;
  brandId?: string;
  userId?: string;
}

export const useRealtimeAPI = ({ systemPrompt, onSpeakingChange, brandId, userId }: UseRealtimeAPIProps = {}) => {
  const [messages, setMessages] = useState<RealtimeMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  const chatRef = useRef<RealtimeChat | null>(null);
  const transcriptRef = useRef<string>('');

  const handleMessage = useCallback((event: any) => {
    console.log('=== Received realtime event ===', event.type, event);

    switch (event.type) {
      case 'session.created':
        console.log('Session created');
        break;

      case 'response.audio.transcript.delta':
        if (event.delta) {
          transcriptRef.current += event.delta;
          setTranscript(transcriptRef.current);
        }
        break;

      case 'response.audio.transcript.done':
        if (transcriptRef.current.trim()) {
          const assistantMessage: RealtimeMessage = {
            id: `assistant-${Date.now()}`,
            content: transcriptRef.current.trim(),
            type: 'assistant',
            timestamp: new Date(),
            isSpoken: true
          };
          console.log('Adding assistant message:', assistantMessage);
          setMessages(prev => {
            const updated = [...prev, assistantMessage];
            console.log('Updated messages array:', updated);
            return updated;
          });
          transcriptRef.current = '';
          setTranscript('');
        }
        break;

      case 'response.audio.delta':
        setAiSpeaking(true);
        onSpeakingChange?.(true);
        break;

      case 'response.audio.done':
        setAiSpeaking(false);
        onSpeakingChange?.(false);
        break;

      case 'response.done':
        // Handle complete response - extract transcript from response.output
        console.log('Complete response received:', event.response);
        if (event.response?.output) {
          for (const item of event.response.output) {
            if (item.type === 'message' && item.content) {
              for (const content of item.content) {
                if (content.type === 'audio' && content.transcript) {
                  const assistantMessage: RealtimeMessage = {
                    id: `assistant-${Date.now()}-complete`,
                    content: content.transcript,
                    type: 'assistant',
                    timestamp: new Date(),
                    isSpoken: true
                  };
                  console.log('Adding complete assistant message:', assistantMessage);
                  setMessages(prev => [...prev, assistantMessage]);
                }
              }
            }
          }
        }
        setAiSpeaking(false);
        onSpeakingChange?.(false);
        break;

      case 'input_audio_buffer.speech_started':
        console.log('User started speaking');
        setIsRecording(true);
        break;

      case 'input_audio_buffer.speech_stopped':
        console.log('User stopped speaking');
        setIsRecording(false);
        break;

      case 'conversation.item.input_audio_transcription.delta':
        // Handle partial user transcription
        if (event.delta) {
          transcriptRef.current += event.delta;
          setTranscript(transcriptRef.current);
        }
        break;

      case 'conversation.item.input_audio_transcription.completed':
        if (event.transcript) {
          const userMessage: RealtimeMessage = {
            id: `user-${Date.now()}`,
            content: event.transcript,
            type: 'user',
            timestamp: new Date(),
            isSpoken: true
          };
          console.log('Adding user message:', userMessage);
          setMessages(prev => {
            const updated = [...prev, userMessage];
            console.log('Updated messages array:', updated);
            return updated;
          });
          // Clear transcript after adding message
          transcriptRef.current = '';
          setTranscript('');
        }
        break;

      case 'error':
        console.error('Realtime API error:', event.error);
        setError(event.error?.message || 'Unknown error');
        break;

      default:
        console.log('Unhandled event type:', event.type);
    }
  }, [onSpeakingChange]);

  const connect = useCallback(async (currentBrandId?: string, currentUserId?: string, tools?: OpenAITool[]) => {
    try {
      setError(null);
      console.log('Connecting to realtime API...');
      console.log('Tools being passed to RealtimeChat:', tools);
      
      chatRef.current = new RealtimeChat(handleMessage, currentBrandId || brandId, currentUserId || userId);
      
      // Set tools BEFORE init so they're used in sendSessionConfig
      if (tools && tools.length > 0) {
        console.log('Setting tools on RealtimeChat before init:', tools.length);
        chatRef.current.setTools(tools);
      }
      
      await chatRef.current.init(currentBrandId || brandId, currentUserId || userId);
      setIsConnected(true);
      
      console.log('Connected to realtime API');
    } catch (error) {
      console.error('Error connecting to realtime API:', error);
      setError(error instanceof Error ? error.message : 'Failed to connect');
      setIsConnected(false);
    }
  }, [handleMessage, brandId, userId]);

  const disconnect = useCallback(() => {
    if (chatRef.current) {
      chatRef.current.disconnect();
      chatRef.current = null;
    }
    setIsConnected(false);
    setIsRecording(false);
    setAiSpeaking(false);
    setTranscript('');
    onSpeakingChange?.(false);
  }, [onSpeakingChange]);

  const startRecording = useCallback(async () => {
    if (!chatRef.current || !isConnected) {
      throw new Error('Not connected');
    }

    try {
      await chatRef.current.startRecording();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      throw error;
    }
  }, [isConnected]);

  const stopRecording = useCallback(() => {
    if (chatRef.current) {
      chatRef.current.stopRecording();
      setIsRecording(false);
    }
  }, []);

  const sendTextMessage = useCallback(async (text: string) => {
    if (!chatRef.current || !isConnected) {
      throw new Error('Not connected');
    }

    try {
      const userMessage: RealtimeMessage = {
        id: `user-${Date.now()}`,
        content: text,
        type: 'user',
        timestamp: new Date(),
        isSpoken: false
      };
      setMessages(prev => [...prev, userMessage]);

      await chatRef.current.sendMessage(text);
    } catch (error) {
      console.error('Error sending text message:', error);
      throw error;
    }
  }, [isConnected]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setTranscript('');
    transcriptRef.current = '';
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chatRef.current) {
        chatRef.current.disconnect();
      }
    };
  }, []);

  return {
    messages,
    isConnected,
    isRecording,
    aiSpeaking,
    transcript,
    error,
    connect,
    disconnect,
    startRecording,
    stopRecording,
    sendTextMessage,
    clearMessages
  };
};